import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Collection } from '../types';
import { useScrollReveal } from '../hooks/useScrollReveal';

const Collections = () => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const containerRef = useScrollReveal([loading]);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await fetch('/api/collections');
        const data = await response.json();
        if (data.success) {
          setCollections(data.data.filter((c: Collection) => c.isActive));
        }
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="spinner-lg" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-offwhite">
      {/* Header */}
      <section className="section-wide pt-section pb-8">
        <div className="reveal">
          <p className="text-[0.6rem] uppercase tracking-[0.25em] text-warmgray-400 mb-2 font-sans">
            Browse
          </p>
          <h1 className="font-display text-display-lg text-offblack">
            Collections
          </h1>
        </div>
      </section>

      {/* Collections Grid */}
      <section className="section-wide pb-section">
        {collections.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {collections.map((collection, index) => (
              <Link
                key={collection._id}
                to={`/collections/${collection._id}`}
                className="reveal group relative aspect-[4/5] overflow-hidden bg-warmgray-200"
                style={{ transitionDelay: `${index * 0.08}s` }}
              >
                {collection.image && (
                  <img
                    src={collection.image.startsWith('http') ? collection.image : collection.image}
                    alt={collection.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-600"
                  />
                )}
                <div className="absolute inset-0 bg-offblack/25 group-hover:bg-offblack/15 transition-colors duration-400" />
                <div className="absolute inset-0 bg-gradient-to-t from-offblack/60 via-transparent to-transparent" />

                <div className="absolute bottom-8 left-8 right-8 z-10">
                  <h2 className="font-display text-xl lg:text-2xl font-semibold text-offwhite mb-2">
                    {collection.name}
                  </h2>
                  {collection.description && (
                    <p className="font-sans text-sm text-offwhite/70 line-clamp-2 mb-3">
                      {collection.description}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-offwhite font-sans opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    View Collection →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-sans text-warmgray-400 mb-6">No collections available yet.</p>
            <Link to="/" className="btn-secondary btn-sm">
              Back to Home
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export default Collections;
